import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import LabelledTextBox from '../../components/LabelledTextBox';
import LabelledPicker from '../../components/LabelledPicker';
import { getHydrantModel, updateHydrantModel, getHydrantManufacturers } from '../../services/api';
import { makeHydrantModel, makeHydrantManufacturer } from '../../models';
import EventEmitter from '../../services/events';

const EditHydrantModel = () => {
  const [hydrantModel, setHydrantModel] = useState(null);
  const [hydrantManufacturers, setHydrantManufacturers] = useState([]);
  const [saving, setSaving] = useState(false);
  const { hydrantModelId } = useParams();
  const history = useHistory();

  useEffect(() => {
    setupHydrantModel();
    setupHydrantManufacturers();
  }, [hydrantModelId]);

  const setupHydrantModel = async () => {
    const result = await getHydrantModel(hydrantModelId);
    setHydrantModel(makeHydrantModel(result));
  };

  const setupHydrantManufacturers = async () => {
    const result = await getHydrantManufacturers();
    setHydrantManufacturers(result.map(r => makeHydrantManufacturer(r)));
  };

  const save = async () => {
    try {
      setSaving(true);
      hydrantModel.validate();
      await updateHydrantModel(hydrantModel);
      history.replace('/hydrantModels');
    } catch (error) {
      console.log(error);
      if (error.name === 'ValidationError') {
        EventEmitter.emit('show-error', { title: 'Error Saving Hydrant Model', body: error.message.split('\n').map((str, idx) => <p key={idx}>{str}</p>) });
      } else {
        EventEmitter.emit('show-error', { title: 'Error Saving Hydrant Model', body: 'Unable to save hydrant model.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    history.goBack();
  };

  const onChange = e => {
    setHydrantModel(makeHydrantModel({ ...hydrantModel, [e.target.name]: e.target.value }));
  };

  const onBlur = e => {
    const type = e.target.type;
    let value = e.target.value;
    switch (type) {
      case 'number':
        value = +value;
        break;
    }
    setHydrantModel(makeHydrantModel({ ...hydrantModel, [e.target.name]: value }));
  };

  if (!hydrantModel) return <div className="spinner"></div>;

  const manufacturerOptions = hydrantManufacturers.map(m => <option key={m.id} value={m.id}>{m.name}</option>);

  return (
    <div>
      <h3>Edit Hydrant Model</h3>
      {saving && <div className="spinner"></div>}
      <div className="details">
        <LabelledTextBox label="Model ID" value={hydrantModel.modelId} name="modelId" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox label="Description" value={hydrantModel.description} name="description" onChange={onChange} onBlur={onBlur} />
        <LabelledPicker label="Manufacturer" value={hydrantModel.hydrantManufacturerId} name="hydrantManufacturerId" onChange={onChange} onBlur={onBlur}>
          <option value=""></option>
          {manufacturerOptions}
        </LabelledPicker>
        <LabelledPicker label="Open Direction" value={hydrantModel.openDirection} name="openDirection" onChange={onChange} onBlur={onBlur}>
          <option value=""></option>
          <option value="Right">Right</option>
          <option value="Left">Left</option>
        </LabelledPicker>
        <LabelledTextBox type="number" label="Valve Turns" value={hydrantModel.valveTurns} name="valveTurns" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Valve Opening Size" value={hydrantModel.valveOpeningSize} name="valveOpeningSize" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox label="Barrel Type" value={hydrantModel.barrelType} name="barrelType" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Pumper Count" value={hydrantModel.pumperCount} name="pumperCount" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Pumper Diameter" value={hydrantModel.pumperDiameter} name="pumperDiameter" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Pumper Coefficient" value={hydrantModel.pumperCoefficient} name="pumperCoefficient" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Pumper Thread Diameter" value={hydrantModel.pumperThreadDiameter} name="pumperThreadDiameter" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Pumper Threads Per Inch" value={hydrantModel.pumperThreadsPerInch} name="pumperThreadsPerInch" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Nozzle Count" value={hydrantModel.nozzleCount} name="nozzleCount" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Nozzle Diameter" value={hydrantModel.nozzleDiameter} name="nozzleDiameter" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Nozzle Coefficient" value={hydrantModel.nozzleCoefficient} name="nozzleCoefficient" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Nozzle Thread Diameter" value={hydrantModel.nozzleThreadDiameter} name="nozzleThreadDiameter" onChange={onChange} onBlur={onBlur} />
        <LabelledTextBox type="number" label="Nozzle Threads Per Inch" value={hydrantModel.nozzleThreadsPerInch} name="nozzleThreadsPerInch" onChange={onChange} onBlur={onBlur} />
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={cancel}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={save}>Save</button>
      </div>
    </div>
  );
};

export default EditHydrantModel;
